import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/config/api";
import { Plus, Eye, FileText, Search } from "lucide-react";
import { TablePagination } from "@/components/TablePagination";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const defaults = {
      date: true,
      customer: true,
      amount: true,
      paymentDue: true,
      invoice: true,
      status: true,
      payment: true,
      assignee: true,
    };
    try {
      const saved = localStorage.getItem("orders.visibleColumns");
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const filtered = useMemo(() => {
    let list = [...(orders || [])];
    const s = (search || "").toLowerCase().trim();
    if (s) {
      list = list.filter(
        (o) =>
          (o.customer_name || "").toLowerCase().includes(s) ||
          (o.assigned_to_name || "").toLowerCase().includes(s) ||
          (o.linked_invoice_number || "").toLowerCase().includes(s) ||
          String(o.id || "")
            .toLowerCase()
            .includes(s),
      );
    }
    if (statusFilter) {
      list = list.filter((o) => (o.status || "").toLowerCase() === statusFilter.toLowerCase());
    }
    return list;
  }, [orders, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const columnCount = 1 + Object.values(visibleColumns).filter(Boolean).length;

  useEffect(() => {
    localStorage.setItem("orders.visibleColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  const fetchOrders = async () => {
    try {
      const data = await api.orders.getAll();
      setOrders(data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString(undefined, {
      dateStyle: "medium",
    });
  };

  const formatMoney = (o, n) => {
    if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
    const cur = o.currency || "OMR";
    return `${cur} ${Number(n).toFixed(2)}`;
  };

  const orderAmount = (o) => {
    if (o.invoice_total != null && o.invoice_total !== "") {
      return Number(o.invoice_total);
    }
    if (o.order_subtotal != null && o.order_subtotal !== "") {
      return Number(o.order_subtotal);
    }
    return null;
  };

  const paymentDueDate = (o) => {
    if (o.invoice_due_date) return o.invoice_due_date;
    return o.due_date || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div className="min-w-0 max-w-full">
          <h1 className="text-2xl font-heading font-semibold text-secondary">Orders</h1>
          <p className="form-helper mt-1 max-w-full">
            Orders are quotes or booking requests. Use &quot;Generate invoice&quot; on an open order to create the <strong>order invoice</strong> (the bill tied to this order). Temporary invoices are for quick bills and created from the Invoices page.
          </p>
        </div>
        <Link
          to="/orders/new"
          className="bg-primary hover:bg-primary/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
        >
          <Plus size={20} />
          New Order
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search by customer or assignee..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStatusFilter("")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${!statusFilter ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("open")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === "open" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}
            >
              Open
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("closed")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === "closed" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}
            >
              Closed
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("cancelled")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === "cancelled" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}
            >
              Cancelled
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-stone-600">
          {[
            ["date", "Date"],
            ["customer", "Customer"],
            ["amount", "Amount"],
            ["paymentDue", "Payment due"],
            ["invoice", "Invoice #"],
            ["status", "Status"],
            ["payment", "Payment"],
            ["assignee", "Assignee"],
          ].map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={visibleColumns[key]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [key]: e.target.checked,
                  }))
                }
              />
              {label}
            </label>
          ))}
        </div>
        <p className="form-helper mt-2">Showing {filtered.length} of {orders.length} order(s)</p>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {visibleColumns.date && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Date
                </th>}
                {visibleColumns.customer && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Customer
                </th>}
                {visibleColumns.amount && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">
                  Amount
                </th>}
                {visibleColumns.paymentDue && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Payment due
                </th>}
                {visibleColumns.invoice && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Invoice
                </th>}
                {visibleColumns.status && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Status
                </th>}
                {visibleColumns.payment && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Payment
                </th>}
                {visibleColumns.assignee && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Assignee
                </th>}
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="py-8 text-center text-stone-500">
                    {orders.length === 0 ? "No orders yet. Create your first order to get started." : "No orders match your search or filter."}
                  </td>
                </tr>
              ) : (
                paginated.map((order) => (
                  <tr
                    key={order.id}
                    className="border-b border-stone-100 hover:bg-stone-50/50"
                  >
                    {visibleColumns.date && <td className="py-3 px-4 text-stone-700">
                      {formatDate(order.created_at)}
                    </td>}
                    {visibleColumns.customer && <td className="py-3 px-4 font-medium text-secondary">
                      {order.customer_name || "—"}
                    </td>}
                    {visibleColumns.amount && <td className="py-3 px-4 text-right font-mono text-sm text-stone-800">
                      {formatMoney(order, orderAmount(order))}
                    </td>}
                    {visibleColumns.paymentDue && <td className="py-3 px-4 text-stone-600 text-sm">
                      {formatDate(paymentDueDate(order))}
                    </td>}
                    {visibleColumns.invoice && <td className="py-3 px-4 text-sm">
                      {order.invoice_id && order.linked_invoice_number ? (
                        <Link
                          to={`/invoices/${order.invoice_id}`}
                          className="font-mono text-primary hover:underline"
                        >
                          {order.linked_invoice_number}
                        </Link>
                      ) : (
                        <span className="text-stone-400">—</span>
                      )}
                    </td>}
                    {visibleColumns.status && <td className="py-3 px-4">
                      <span
                        title={
                          order.status === "cancelled" && order.cancelled_reason
                            ? `Reason: ${order.cancelled_reason}`
                            : undefined
                        }
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium uppercase ${
                          order.status === "closed"
                            ? "bg-emerald-100 text-emerald-700"
                            : order.status === "cancelled"
                              ? "bg-rose-100 text-rose-700"
                              : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {order.status}
                      </span>
                      {order.status === "cancelled" && order.cancelled_reason && (
                        <p className="mt-1 text-xs text-rose-700 max-w-[220px] truncate">
                          {order.cancelled_reason}
                        </p>
                      )}
                    </td>}
                    {visibleColumns.payment && <td className="py-3 px-4 text-stone-600 capitalize">
                      {(order.payment_type || "—").replace("_", " ")}
                    </td>}
                    {visibleColumns.assignee && <td className="py-3 px-4 text-stone-600 text-sm">
                      {order.assigned_to_name || "—"}
                    </td>}
                    <td className="py-3 px-4 text-right">
                      <Link
                        to={`/orders/${order.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      >
                        <Eye size={16} />
                        View
                      </Link>
                      {order.status === "open" && (
                        <Link
                          to={`/orders/${order.id}?generateInvoice=1`}
                          className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        >
                          <FileText size={16} />
                          Generate invoice
                        </Link>
                      )}
                      {order.status === "closed" && order.invoice_id && (
                        <Link
                          to={`/invoices/${order.invoice_id}`}
                          className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                        >
                          <FileText size={16} />
                          View invoice
                        </Link>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={filtered.length}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            sizeOptions={[5, 10, 15, 20, 25, 30, 40, 50, 100, 200, 500]}
          />
        )}
      </div>
    </div>
  );
}
